import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { memo, useMemo } from "react";
import { ChevronDown } from "lucide-react";
import { FileAttachmentScope, FileAttachmentType, PLACEHOLDER_ASSISTANT_ID, } from "@f2e/shared/types";
import { conversationPartToReasoningStep, getResumableHistoryAnalysisToolCalls, stripLegacyToolTags, } from "../../../api/codeer";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger, } from "../../../components/base";
import { InteractionRequestForm, } from "../../../components/InteractionRequestForm";
import { useUiTranslation } from "../../../i18n";
import { PaymentSummaryCard, parsePaymentSummaryContent, } from "../PaymentSummaryCard";
import { MessageBlock } from "./MessageBlock";
import { MessageBubble } from "./MessageBubble";
import { getReasoningStepTypeLabels } from "./reasoningStepLabels";
import { ReasoningStepRenderer, } from "../feedback/ReasoningStepRenderer";
import { TurnErrorNotice } from "../feedback/TurnErrorNotice";
export const getConversationPartText = (part) => {
    const content = part.content.content;
    if (typeof content === "string")
        return stripLegacyToolTags(content);
    if (content == null)
        return "";
    if (typeof content === "number" || typeof content === "boolean") {
        return String(content);
    }
    try {
        return JSON.stringify(content);
    }
    catch {
        return String(content);
    }
};
const getToolId = (part) => {
    const toolCallId = part.content.tool_call_id;
    if (typeof toolCallId === "string" && toolCallId.trim()) {
        return toolCallId;
    }
    return part.id != null ? `part-${part.id}` : undefined;
};
const getRequestFormInteraction = (part) => {
    const interaction = part.metadata?.interaction;
    return interaction?.type === "request_form" ? interaction : undefined;
};
const isFinishedToolPart = (part) => {
    if (part.part_kind !== "tool-call" && part.part_kind !== "tool-return") {
        return false;
    }
    const toolName = part.content.tool_name;
    const reasoningStepType = part.metadata.reasoning_step_type;
    return (toolName === "finished" ||
        toolName === "consultant_finished" ||
        reasoningStepType === "finished" ||
        reasoningStepType === "consultant_finished");
};
const getToolStep = (toolCallPart, groupParts) => {
    const callStep = conversationPartToReasoningStep(toolCallPart);
    if (!callStep)
        return undefined;
    const toolId = getToolId(toolCallPart);
    const returnPart = toolId
        ? groupParts.find((part) => part.part_kind === "tool-return" &&
            part.content.tool_call_id === toolId)
        : undefined;
    const returnStep = returnPart
        ? conversationPartToReasoningStep(returnPart)
        : undefined;
    return returnStep
        ? {
            ...callStep,
            ...returnStep,
            args: callStep.args,
            startTime: callStep.startTime,
            content: returnStep.content || callStep.content,
        }
        : callStep;
};
export const conversationPartToChatMessage = (part, role, filterAttachment, externalUserIdFallback, applyTargetPath) => {
    const outputComponents = getOutputComponents(part);
    return {
        id: part.id ?? part.conversation_id ?? undefined,
        groupId: part.conversation_group_id,
        role,
        content: getConversationPartText(part),
        externalUserId: role === "user"
            ? (part.external_user_id ?? externalUserIdFallback ?? undefined)
            : undefined,
        meta: role === "assistant" && (outputComponents.length > 0 || applyTargetPath)
            ? { outputComponents, applyTargetPath: applyTargetPath ?? undefined }
            : undefined,
        attachedFiles: part.attached_files
            ?.map((file) => ({
            id: file.id,
            uuid: file.id,
            name: file.name,
            type: file.type,
            url: file.url,
            meta: file.meta,
            status: "uploaded",
            scope: file.scope === FileAttachmentScope.Persistent ||
                file.scope === FileAttachmentScope.Ephemeral
                ? file.scope
                : undefined,
            attachmentType: file.attachment_type === FileAttachmentType.File ||
                file.attachment_type === FileAttachmentType.ContextObject ||
                file.attachment_type === FileAttachmentType.WebContent ||
                file.attachment_type === FileAttachmentType.PageSnapshot
                ? file.attachment_type
                : undefined,
        }))
            .filter((file) => filterAttachment?.(file, part) ?? true),
        createdAt: part.created_at ?? undefined,
        helpfulSubmitted: Boolean(part.metadata.helpful_submitted) ||
            (part.feedback_counts?.sys_helpful ?? 0) > 0 ||
            hasSystemFeedback(part, "sys_helpful"),
        improveSubmitted: Boolean(part.metadata.improve_submitted) ||
            (part.feedback_counts?.sys_improve ?? 0) > 0 ||
            hasSystemFeedback(part, "sys_improve"),
    };
};
const getOutputComponents = (part) => {
    const metadataComponents = part.metadata.output_components;
    return Array.isArray(metadataComponents) ? metadataComponents : [];
};
const hasSystemFeedback = (part, type) => part.feedbacks?.some((feedback) => feedback.tag === "system" && feedback.type === type) ?? false;
const isRenderablePart = (part, groupParts, isAsking) => {
    if (part.part_kind === "system-prompt")
        return false;
    if (part.part_kind === "thinking")
        return false;
    if (part.part_kind === "tool-return")
        return false;
    if (isFinishedToolPart(part))
        return false;
    if (part.part_kind !== "text")
        return true;
    if (getConversationPartText(part).trim())
        return true;
    // Console labels carry the attachment, operator and delivery controls even
    // when the human deliberately sends a file without a text caption.
    if (part.metadata.source === "platform_reply" &&
        part.metadata.platform_reply?.attachment)
        return true;
    const partIndex = groupParts.indexOf(part);
    const hasLaterToolOrText = groupParts
        .slice(partIndex + 1)
        .some((next) => next.part_kind === "tool-call" || next.part_kind === "text");
    return isAsking && !hasLaterToolOrText;
};
const getAssistantResponseMeta = (part) => {
    const response = part?.metadata.assistant_response ?? part?.metadata.assistantResponse;
    if (!response || typeof response !== "object")
        return undefined;
    if (typeof response.status !== "string") {
        return undefined;
    }
    return response;
};
const shouldShowFailedTurnNotice = (assistantResponse) => assistantResponse?.status === "failed" ||
    assistantResponse?.status === "timeout" ||
    assistantResponse?.status === "cancelled";
const isAssistantPlaceholder = (part) => part.id === PLACEHOLDER_ASSISTANT_ID && part.part_kind === "text";
const defaultPaymentSummaryLabels = {
    title: "Payment Request",
    defaultItemDesc: "Payment request",
    amount: "Amount",
    orderNo: "Order No.",
    paidAt: "Paid At",
    tradeNo: "Trade No.",
    paymentMethod: "Payment Method",
    getStatusLabel: (status) => status,
};
const MessageTimestamp = ({ value, locale, }) => value ? (_jsx("div", { className: "mt-2 text-xs text-muted-foreground", children: new Date(value).toLocaleString(locale) })) : null;
export const ToolPartCard = ({ step, label, hideSensitiveReasoningDetails = false, showSafeTabularSummary = false, showSafeKnowledgeBaseSummary = false, showSafeHttpRequestSummary = false, resolveUrl, onCancelHistoryAnalysis, defaultOpen = false, }) => (_jsx("div", { className: "py-2 mb-4 rounded-md border border-dashed border-zinc-300 dark:border-zinc-700 bg-zinc-50/80 dark:bg-zinc-900/40 [&_h3]:m-0 [&_h3]:font-normal [&_h3]:text-sm [&_h3]:leading-tight", children: _jsx(Accordion, { type: "single", collapsible: true, defaultValue: defaultOpen ? step.id : undefined, children: _jsxs(AccordionItem, { value: step.id, className: "border-0", children: [_jsx(AccordionTrigger, { className: "px-3 py-0 my-0 hover:no-underline [&>svg]:hidden", children: _jsxs("div", { className: "flex w-full items-center justify-between gap-2 text-muted-foreground", children: [_jsx("span", { children: label }), _jsx(ChevronDown, { className: "h-3 w-3 opacity-60" })] }) }), _jsx(AccordionContent, { className: "pb-0", children: _jsx("div", { className: "max-h-80 overflow-auto px-3 pb-0 leading-relaxed text-foreground", children: _jsx(ReasoningStepRenderer, { step: step, hideSensitiveReasoningDetails: hideSensitiveReasoningDetails, showSafeTabularSummary: showSafeTabularSummary, showSafeKnowledgeBaseSummary: showSafeKnowledgeBaseSummary, showSafeHttpRequestSummary: showSafeHttpRequestSummary, resolveUrl: resolveUrl, onCancelHistoryAnalysis: onCancelHistoryAnalysis }) }) })] }) }, defaultOpen ? "open" : "closed") }));
const PartNativeConversationPanelComponent = ({ className, history, parts, isAsking = false, showExternalUserId = false, assistantActionBarMode, timestampLocale, hideSensitiveReasoningDetails = false, resolveUrl, onDownload, onPreviewLoad, onFormImageLoad = onPreviewLoad, onEditImage, onHelpfulFeedback, onImproveFeedback, onFeedbackBlocked, submittingFormRequestId, onSubmitFormInteraction, onRejectFormInteraction, onCancelHistoryAnalysis, showHistoryAnalysisResumeGuidance = false, regenerateDisabled = false, regenerateDisabledTooltipContent, onRegenerate, paymentSummaryLabels = defaultPaymentSummaryLabels, renderAssistantActionBar, renderGroupFooter, renderGroupSeparator, renderBetweenParts, renderPartLabel, renderPart, renderPaymentSummary, filterAttachment, externalUserIdFallback, getMessageContainerClassName, getPartWrapperClassName, shouldShowAssistantActionBar, messageBlockExtensions, }) => {
    const { t: uiT } = useUiTranslation();
    const stepTypeLabels = useMemo(() => getReasoningStepTypeLabels(uiT), [uiT]);
    const resolvedAssistantActionBarMode = assistantActionBarMode ?? (renderAssistantActionBar ? "custom" : "default");
    const latestGroupId = useMemo(() => {
        const latestPart = [...parts]
            .reverse()
            .find((part) => part.part_kind !== "system-prompt");
        return latestPart?.conversation_group_id;
    }, [parts]);
    const contexts = useMemo(() => {
        const groupIds = [];
        const groupPartsById = new Map();
        for (const part of parts) {
            const groupId = part.conversation_group_id;
            if (!groupPartsById.has(groupId)) {
                groupIds.push(groupId);
                groupPartsById.set(groupId, []);
            }
            groupPartsById.get(groupId).push(part);
        }
        return new Map(parts.map((part) => {
            const groupParts = groupPartsById.get(part.conversation_group_id) ?? [];
            const lastRenderablePart = [...groupParts]
                .reverse()
                .find((item) => isRenderablePart(item, groupParts, isAsking));
            const context = {
                groupId: part.conversation_group_id,
                groupIndex: groupIds.indexOf(part.conversation_group_id),
                groupParts,
                isLastRenderablePartInGroup: lastRenderablePart === part,
            };
            return [part, context];
        }));
    }, [isAsking, parts]);
    const renderParts = useMemo(() => {
        const assistantPlaceholders = parts.filter(isAssistantPlaceholder);
        if (assistantPlaceholders.length === 0)
            return parts;
        return [
            ...parts.filter((part) => !isAssistantPlaceholder(part)),
            ...assistantPlaceholders,
        ];
    }, [parts]);
    const resumableHistoryAnalysisParts = useMemo(() => new Set(getResumableHistoryAnalysisToolCalls(parts)), [parts]);
    const processingGroupId = useMemo(() => {
        if (!isAsking)
            return undefined;
        const latestRenderablePart = [...parts].reverse().find((part) => {
            const context = contexts.get(part);
            return context
                ? isRenderablePart(part, context.groupParts, isAsking)
                : false;
        });
        return latestRenderablePart?.conversation_group_id;
    }, [contexts, isAsking, parts]);
    const renderedToolIds = new Set();
    let previousRenderedContext;
    return (_jsx("div", { className: className, children: _jsx("div", { className: "space-y-4 px-4", "data-testid": "part-native-conversation-panel", children: renderParts.map((part, partIndex) => {
                const context = contexts.get(part);
                if (!context ||
                    !isRenderablePart(part, context.groupParts, isAsking)) {
                    return null;
                }
                const separator = previousRenderedContext &&
                    previousRenderedContext.groupId !== context.groupId
                    ? renderGroupSeparator?.(previousRenderedContext, context)
                    : null;
                previousRenderedContext = context;
                let node = null;
                const questionPart = context.groupParts.find((item) => item.part_kind === "user-prompt");
                const assistantResponse = getAssistantResponseMeta(questionPart);
                const shouldRenderFailedTurnNotice = context.isLastRenderablePartInGroup &&
                    context.groupId === latestGroupId &&
                    shouldShowFailedTurnNotice(assistantResponse);
                if (part.part_kind === "tool-call") {
                    const toolId = getToolId(part);
                    const formInteraction = getRequestFormInteraction(part);
                    if (formInteraction) {
                        if (toolId)
                            renderedToolIds.add(toolId);
                        const canSubmit = formInteraction.status === "pending" &&
                            formInteraction.can_submit !== false &&
                            !!onSubmitFormInteraction;
                        node = (_jsx("div", { className: "my-4", children: _jsx(InteractionRequestForm, { schema: formInteraction.schema, onPreviewLoad: onFormImageLoad, initialValues: formInteraction.submission ?? undefined, readOnly: !canSubmit, onSubmit: (values) => onSubmitFormInteraction?.(part, values), onReject: canSubmit && onRejectFormInteraction
                                    ? () => onRejectFormInteraction(part)
                                    : undefined, submitting: submittingFormRequestId ===
                                    formInteraction.form_request_id }) }));
                    }
                    else {
                        const step = getToolStep(part, context.groupParts);
                        if (!toolId || !step || renderedToolIds.has(toolId))
                            return null;
                        const historyAnalysisInterrupted = showHistoryAnalysisResumeGuidance &&
                            !isAsking &&
                            resumableHistoryAnalysisParts.has(part);
                        const renderedStep = historyAnalysisInterrupted
                            ? {
                                ...step,
                                args: {
                                    ...step.args,
                                    history_analysis: {
                                        ...step.args?.history_analysis,
                                        interrupted: true,
                                    },
                                },
                            }
                            : step;
                        renderedToolIds.add(toolId);
                        node = (_jsx(ToolPartCard, { step: renderedStep, defaultOpen: historyAnalysisInterrupted, hideSensitiveReasoningDetails: hideSensitiveReasoningDetails, resolveUrl: resolveUrl, onCancelHistoryAnalysis: onCancelHistoryAnalysis, label: stepTypeLabels[step.type] ??
                                step.content }));
                    }
                }
                else {
                    const isUser = part.part_kind === "user-prompt";
                    const message = conversationPartToChatMessage(part, isUser ? "user" : "assistant", filterAttachment, externalUserIdFallback, questionPart?.metadata?.apply_target_path);
                    const paymentSummary = parsePaymentSummaryContent(message.content);
                    const isLastAssistantText = !isUser &&
                        context.groupParts
                            .slice(context.groupParts.indexOf(part) + 1)
                            .every((next) => next.part_kind !== "text");
                    let customActionBar;
                    let usesCustomActionBarTimestamp = false;
                    if (!isUser) {
                        if (resolvedAssistantActionBarMode === "hidden" ||
                            shouldShowAssistantActionBar?.(context, part) === false) {
                            customActionBar = _jsx(_Fragment, {});
                        }
                        else if (resolvedAssistantActionBarMode === "custom") {
                            if (isLastAssistantText &&
                                questionPart &&
                                renderAssistantActionBar) {
                                usesCustomActionBarTimestamp = true;
                                customActionBar = renderAssistantActionBar({
                                    context,
                                    message,
                                    questionPart,
                                    questionContent: getConversationPartText(questionPart),
                                    answerPart: part,
                                    allParts: parts,
                                });
                            }
                            else {
                                customActionBar = _jsx(_Fragment, {});
                            }
                        }
                    }
                    const timestampNode = (_jsx(MessageTimestamp, { value: usesCustomActionBarTimestamp && !paymentSummary
                            ? undefined
                            : message.createdAt, locale: timestampLocale }));
                    node = isUser ? (_jsxs(_Fragment, { children: [showExternalUserId && message.externalUserId?.trim() && (_jsx("div", { className: "text-sm text-muted-foreground text-right", children: message.externalUserId })), _jsx(MessageBubble, { message: message, timestampLocale: timestampLocale, resolveUrl: resolveUrl, onDownload: onDownload, onPreviewLoad: onPreviewLoad })] })) : paymentSummary ? (_jsxs(_Fragment, { children: [renderPaymentSummary?.(context, message, paymentSummary, _jsx(PaymentSummaryCard, { summary: paymentSummary, labels: paymentSummaryLabels }), partIndex) ?? (_jsx(PaymentSummaryCard, { summary: paymentSummary, labels: paymentSummaryLabels })), timestampNode] })) : (_jsxs(_Fragment, { children: [_jsx("div", { className: [
                                    "-mx-3 rounded-2xl px-4 py-3 sm:-mx-4 sm:px-5",
                                    getMessageContainerClassName?.(context, part),
                                ]
                                    .filter(Boolean)
                                    .join(" "), children: _jsx(MessageBlock, { history: history, message: message, extensions: messageBlockExtensions, resolveUrl: resolveUrl, onDownload: onDownload, onPreviewLoad: onPreviewLoad, onEditImage: onEditImage, isProcessing: isAsking &&
                                        isLastAssistantText &&
                                        context.groupId === processingGroupId, customActionBar: customActionBar, onHelpfulFeedback: () => onHelpfulFeedback?.(part) ?? Promise.resolve(false), onImproveFeedback: (content) => onImproveFeedback?.(part, content) ??
                                        Promise.resolve(false), onFeedbackBlocked: onFeedbackBlocked }) }), timestampNode] }));
                }
                const defaultPartNode = (_jsxs(_Fragment, { children: [renderPartLabel?.(context, part, partIndex), node] }));
                return (_jsxs("div", { className: getPartWrapperClassName?.(context, part), children: [separator, renderPart?.(context, part, defaultPartNode, partIndex) ??
                            defaultPartNode, shouldRenderFailedTurnNotice && (_jsx(TurnErrorNotice, { status: assistantResponse?.status, error: assistantResponse?.error, retryable: assistantResponse?.retryable, isAsking: isAsking, disabled: regenerateDisabled, disabledTooltipContent: regenerateDisabledTooltipContent, onRegenerate: onRegenerate
                                ? () => onRegenerate(context.groupId)
                                : undefined })), renderBetweenParts?.(context, part, partIndex), context.isLastRenderablePartInGroup &&
                            renderGroupFooter?.(context, context.groupIndex)] }, `${part.id ?? "part"}-${partIndex}`));
            }) }) }));
};
export const PartNativeConversationPanel = memo(PartNativeConversationPanelComponent);
export default PartNativeConversationPanel;
//# sourceMappingURL=PartNativeConversationPanel.js.map